import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Paper,
  Container,
  Avatar,
  Breadcrumbs,
  Link,
  useTheme,
  alpha,
} from "@mui/material";
import {
  PermIdentityTwoTone as ProfileIcon,
  SettingsOutlined as SettingsIcon,
  ChevronRight as ChevronRightIcon,
  HomeOutlined as HomeIcon,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import Layout from "../components/layout/Layout";
import Profile from "./Profile";
import Billing from "./Billing";
import Security from "./Security";
import Notification from "./Notification";
import { appsPages } from "../dashboardRow";

const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut",
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: "easeOut" },
  },
};

const tabContentVariants = {
  hidden: { opacity: 0, x: 20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.5, ease: "easeOut" },
  },
  exit: {
    opacity: 0,
    x: -20,
    transition: { duration: 0.3, ease: "easeIn" },
  },
};

const AccountProfile = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const params = new URLSearchParams(location.search);
  const selectedTab = params.get("page");

  const [value, setValue] = useState(0);

  const initialValue = selectedTab
    ? appsPages[0].apps.findIndex((app) => app.name === selectedTab)
    : 0;

  useEffect(() => {
    if (initialValue !== -1) {
      setValue(initialValue);
    }
  }, [initialValue]);

  const handleTabChange = (event, newValue) => {
    setValue(newValue);
    const selectedTabName = appsPages[0].apps[newValue].name;
    navigate(`?page=${selectedTabName}`, { replace: true });
  };

  const currentTab = appsPages[0].apps[value];

  const renderTabContent = () => {
    switch (value) {
      case 0:
        return <Profile />;
      case 1:
        return <Billing />;
      case 2:
        return <Security />;
      case 3:
        return <Notification />;
      default:
        return <Profile />;
    }
  };

  return (
    <Layout>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <Container maxWidth="xl" sx={{ py: 3 }}>
          <motion.div variants={itemVariants}>
            <Breadcrumbs
              separator={<ChevronRightIcon fontSize="small" />}
              sx={{ mb: 3 }}
            >
              <Link
                color="inherit"
                href="/Dashboard"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  textDecoration: "none",
                  fontSize: "0.875rem",
                  "&:hover": { color: theme.palette.primary.main },
                }}
              >
                <HomeIcon sx={{ mr: 0.5, fontSize: "1rem" }} />
                Dashboard
              </Link>
              <Typography
                sx={{
                  display: "flex",
                  alignItems: "center",
                  fontSize: "0.875rem",
                  color: theme.palette.text.secondary,
                }}
              >
                <SettingsIcon sx={{ mr: 0.5, fontSize: "1rem" }} />
                Account Settings
              </Typography>
            </Breadcrumbs>
          </motion.div>

          <motion.div variants={itemVariants}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                mb: 3,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                color: "white",
                borderRadius: 3,
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  right: 0,
                  width: "200px",
                  height: "200px",
                  background: `radial-gradient(circle, ${alpha(
                    theme.palette.primary.light,
                    0.3
                  )} 0%, transparent 70%)`,
                  transform: "translate(50px, -50px)",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  zIndex: 1,
                  position: "relative",
                }}
              >
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      bgcolor: alpha(theme.palette.common.white, 0.2),
                      mr: 3,
                      fontSize: "2rem",
                    }}
                  >
                    <ProfileIcon fontSize="inherit" />
                  </Avatar>
                </motion.div>
                <Box>
                  <Typography
                    variant="h4"
                    component="h1"
                    sx={{ fontWeight: 700, mb: 1 }}
                  >
                    Account Settings
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ opacity: 0.9, fontWeight: 400 }}
                  >
                    Manage your {currentTab.name.toLowerCase()} preferences
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </motion.div>

          <motion.div variants={itemVariants}>
            <Paper
              elevation={1}
              sx={{
                borderRadius: 3,
                overflow: "hidden",
                bgcolor: theme.palette.background.paper,
              }}
            >
              <Box
                sx={{
                  borderBottom: 1,
                  borderColor: "divider",
                  bgcolor: alpha(theme.palette.secondary.main, 0.3),
                  px: 2,
                }}
              >
                <Tabs
                  value={value}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    "& .MuiTab-root": {
                      minHeight: 64,
                      textTransform: "none",
                      fontSize: "1rem",
                      fontWeight: 500,
                      mx: 1,
                      borderRadius: 2,
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        transform: "translateY(-1px)",
                      },
                      "&.Mui-selected": {
                        color: theme.palette.primary.main,
                        fontWeight: 600,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                      },
                    },
                    "& .MuiTabs-indicator": {
                      height: 3,
                      borderRadius: "3px 3px 0 0",
                      background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                    },
                  }}
                >
                  {appsPages[0].apps.map((app, index) => (
                    <Tab
                      key={app.id}
                      label={
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          {React.createElement(app.icon || ProfileIcon, {
                            fontSize: "small",
                          })}
                          <span>{app.name}</span>
                        </Box>
                      }
                    />
                  ))}
                </Tabs>
              </Box>

              <Box
                sx={{
                  position: "relative",
                  minHeight: "60vh",
                  width: "100%",
                }}
              >
                <AnimatePresence mode="wait">
                  <motion.div
                    key={value}
                    variants={tabContentVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    style={{
                      width: "100%",
                      minHeight: "100%",
                      padding: theme.spacing(4),
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        minHeight: "100%",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      {renderTabContent()}
                    </Box>
                  </motion.div>
                </AnimatePresence>
              </Box>
            </Paper>
          </motion.div>
        </Container>
      </motion.div>
    </Layout>
  );
};

export default AccountProfile;
